// Google OAuth utility and token management
import { gapi } from 'gapi-script';

// Configuration - Replace with your actual Google Cloud Console credentials
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || null;
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || null;
const SCOPES = [
  "https://www.googleapis.com/auth/calendar.events",
  "https://www.googleapis.com/auth/gmail.readonly",
  "https://www.googleapis.com/auth/gmail.send",
  "https://www.googleapis.com/auth/gmail.modify",
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile"
].join(" ");

let isGapiInitialized = false;

/**
 * Initialize Google API client
 */
export const initializeGoogleAPI = () => {
  return new Promise((resolve, reject) => {
    // Skip initialization if no credentials are provided
    if (!CLIENT_ID || !API_KEY) {
      // Only log once in development mode to avoid console spam
      if (process.env.NODE_ENV === 'development' && !window._googleAuthWarningShown) {
        console.info('💡 Optional: Google Calendar integration available - see GOOGLE_INTEGRATION_GUIDE.md');
        window._googleAuthWarningShown = true;
      }
      reject(new Error('Google API credentials not configured'));
      return;
    }

    if (isGapiInitialized) {
      resolve(true);
      return;
    }

    const handleGapiLoad = () => {
      gapi.load('auth2:client', {
        callback: async () => {
          try {
            await gapi.client.init({
              apiKey: API_KEY,
              clientId: CLIENT_ID,
              discoveryDocs: [
                'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest',
                'https://www.googleapis.com/discovery/v1/apis/gmail/v1/rest'
              ],
              scope: SCOPES
            });

            isGapiInitialized = true;
            console.info('✅ Google API initialized successfully');
            resolve(true);
          } catch (error) {
            console.error('❌ Google API initialization failed:', error);
            reject(error);
          }
        },
        onerror: (error) => {
          console.error('❌ Failed to load GAPI:', error);
          reject(error);
        }
      });
    };

    if (typeof gapi !== 'undefined' && gapi.load) {
      handleGapiLoad();
    } else {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = handleGapiLoad;
      script.onerror = () => reject(new Error('Failed to load Google API script'));
      document.head.appendChild(script);
    }
  });
};

/**
 * Check if user is connected to Google
 */
export const isGoogleConnected = () => {
  try {
    // Check if API is initialized and has auth instance
    if (!isGapiInitialized || !gapi?.auth2) {
      return false;
    }

    const authInstance = gapi.auth2.getAuthInstance();
    if (!authInstance) {
      return false;
    }

    return authInstance.isSignedIn.get();
  } catch (error) {
    // Silently fail - user just won't have Google integration
    return false;
  }
};

/**
 * Sign in to Google
 */
export const signInToGoogle = async () => {
  try {
    await initializeGoogleAPI();
    
    const authInstance = gapi.auth2.getAuthInstance();
    if (!authInstance) {
      throw new Error('Google Auth not initialized');
    }

    const user = await authInstance.signIn();
    const accessToken = user.getAuthResponse().access_token;
    
    // Store the token
    storeGoogleToken(accessToken);
    
    console.info('✅ Successfully signed in to Google');
    return user;
  } catch (error) {
    console.error('❌ Google sign-in failed:', error);
    throw error;
  }
};

/**
 * Sign out from Google
 */
export const signOutFromGoogle = async () => {
  try {
    if (!isGoogleConnected()) {
      return;
    }

    const authInstance = gapi.auth2.getAuthInstance();
    if (authInstance) {
      await authInstance.signOut();
    }
    
    // Clear stored token
    removeGoogleToken();
    
    console.info('✅ Successfully signed out from Google');
  } catch (error) {
    console.error('❌ Google sign-out failed:', error);
    // Clear token anyway in case of error
    removeGoogleToken();
    throw error;
  }
};

/**
 * Get current access token
 */
export const getGoogleToken = () => {
  try {
    // First try to get from current session
    if (isGoogleConnected()) {
      const authInstance = gapi.auth2.getAuthInstance();
      const user = authInstance.currentUser.get();
      if (user.isSignedIn()) {
        const authResponse = user.getAuthResponse();
        if (authResponse && authResponse.access_token) {
          return authResponse.access_token;
        }
      }
    }
    
    // Fallback to localStorage
    return localStorage.getItem('google_access_token');
  } catch (error) {
    // Fallback to localStorage if gapi fails
    return localStorage.getItem('google_access_token');
  }
};

/**
 * Store access token
 */
export const storeGoogleToken = (token) => {
  if (token) {
    localStorage.setItem('google_access_token', token);
  }
};

/**
 * Remove access token
 */
export const removeGoogleToken = () => {
  localStorage.removeItem('google_access_token');
};

/**
 * Refresh access token
 */
export const refreshGoogleToken = async () => {
  try {
    if (!isGoogleConnected()) {
      throw new Error('User not signed in');
    }

    const authInstance = gapi.auth2.getAuthInstance();
    const user = authInstance.currentUser.get();
    
    const newAuthResponse = await user.reloadAuthResponse();
    const newToken = newAuthResponse.access_token;
    
    storeGoogleToken(newToken);
    return newToken;
  } catch (error) {
    console.error('❌ Failed to refresh Google token:', error);
    throw error;
  }
};

// Alias for backward compatibility
export const setGoogleToken = storeGoogleToken;
export const clearGoogleToken = removeGoogleToken;
